import * as ents from '@attain-apps/entities'
import * as store from '@attain-apps/fe/web/main/lib/store'
import * as rxjs from 'rxjs'

import { SPACE_RECORDS_SELECTION_TARGET } from '../../../constants'
import type { Plugin } from '../plugin'

const SUBMIT_CANCELLATION_REASON = {
  noContext: 'No scenario context available',
  noMethodology: 'No methodology to apply',
  noSelectedRecords: 'No Space records selected for the bulk update',
  noUpdate: 'No bulk functional usage update in progress',
} as const

/**
 * Applies the edited functional usage to every selected record.
 */
export const executeSubmit = store.effects.create(
  (feature: Plugin['feature'], plugin: Plugin) =>
    feature.actions.submit.request.observe().pipe(
      rxjs.withLatestFrom(
        feature.selectors.methodology.asObservable,
        feature.selectors.functionalizations.asObservable,
        feature.selectors.serviceCenterId.asObservable,
        plugin.collection.feature.selectors.context.asObservable,
        plugin.collection.plugins.selectionPlugin.feature.selectors.getAllSelectedIds(
          {
            target: SPACE_RECORDS_SELECTION_TARGET,
          },
        ).asObservable,
      ),
      rxjs.mergeMap(
        ([
          action,
          functionalizationMethodology,
          functionalizations,
          serviceCenterId,
          context,
          selectedIds,
        ]) => {
          if (
            !ents.extra.space.Record.FunctionalUsage.Methodology.isMethodology(
              functionalizationMethodology,
            )
          ) {
            return rxjs.of(
              feature.actions.submit.cancellation
                .from(action)
                .create({ reason: SUBMIT_CANCELLATION_REASON.noMethodology }),
            )
          }

          if (!ents.core.admin.Context.ForScenario.isForScenario(context)) {
            return rxjs.of(
              feature.actions.submit.cancellation
                .from(action)
                .create({ reason: SUBMIT_CANCELLATION_REASON.noContext }),
            )
          }

          const spaceRecordIds = selectedIds.filter(
            ents.extra.space.Record.Id.isId,
          )

          if (!spaceRecordIds.length) {
            return rxjs.of(
              feature.actions.submit.cancellation.from(action).create({
                reason: SUBMIT_CANCELLATION_REASON.noSelectedRecords,
              }),
            )
          }

          return plugin.api
            .update({
              context: { ...context, spaceRecordIds },
              updates: {
                functionalizationMethodology,
                functionalizations,
                serviceCenterId,
              },
            })
            .pipe(
              rxjs.map(() =>
                feature.actions.submit.success.from(action).create(),
              ),
              rxjs.catchError((error: Error) =>
                rxjs.of(
                  feature.actions.submit.error.from(action).create({ error }),
                ),
              ),
            )
        },
      ),
    ),
)
